export const symptomTracking = {
    slug: 'symptom-tracking',
    no: '04',
    type: 'rich',
    title: 'Symptom Tracking',
    subtitle: 'A mobile app for chronic disease patients',
    category: 'Mobile App',
    sourceUrl: 'https://himanshutanty.com/portfolio/symptom-tracking/',
    cover: '/api/files/9841308f-afa7-4b94-ba63-c91d752f8d13',
    client: 'Concept · Healthcare',
    timeline: 'April 2023',
    responsibilities: [
        'Discovery research',
        'Persona creation',
        'User flows',
        'Sketches & wireframes',
        'Visual design & style guide',
        'Prototype',
    ],
    overview:
        'A concept mobile app for patients managing chronic diseases. The product helps them log symptoms, track medications, share updates with providers, and access curated educational content, designed to improve adherence, sustain engagement, and strengthen the loop between patients and their care teams.',
    prototype:
        'https://www.figma.com/proto/4FWUr6x7hrFWJMZcBFlFf8/Assignments?page-id=0%3A1&node-id=229-107517&viewport=291%2C-17226%2C0.2&scaling=scale-down&starting-point-node-id=223%3A47005&show-proto-sidebar=1',
    blocks: [
        // ── Project description (from source) ───────────────────────
        {
            kind: 'section',
            heading: 'About the project',
            subheading: 'Mobile App for Symptom Tracking and Management',
            paragraph:
                'The goal of this project is to develop a mobile app that allows patients to manage and track their symptoms for chronic diseases. The app will provide patients with a range of features to help them record their symptoms, track medications, communicate with healthcare providers, and access educational resources, designed to improve patient adherence, increase engagement, and enhance communication between patients and their care teams.',
        },
        {
            kind: 'section',
            heading: 'The goal',
            paragraph:
                'Help patients better manage their chronic disease by providing tools to track symptoms and medication, educate them over time, communicate with healthcare providers, and access educational resources.',
        },
        {
            kind: 'bullets',
            heading: 'Design principles',
            items: [
                'User-friendly interface for easy symptom tracking and data sharing with healthcare providers',
                'Customizable options for tracking different symptoms and treatment plans based on individual needs',
                'Integration with wearable devices and other health apps for comprehensive health monitoring',
                'Privacy and security measures to protect patient data and comply with healthcare regulations',
            ],
        },
        // ── Design-Thinking walk-through ────────────────────────────
        {
            kind: 'tabs',
            tabs: [
                // ── Empathise ────────────────────────────────────────
                {
                    label: 'Empathise',
                    blocks: [
                        {
                            kind: 'phase-hero',
                            phase: 'empathise',
                            number: '01',
                            title: 'Empathise',
                            items: [
                                'Challenges & pain-points',
                                'Market recognition',
                                'Patient interviews',
                            ],
                        },
                        {
                            kind: 'section',
                            heading: 'Challenges & pain-points',
                        },
                        {
                            kind: 'bullets',
                            items: [
                                'Patients with chronic diseases struggle to manage their symptoms on a daily basis, affecting their quality of life.',
                                'Inability to track symptoms accurately makes it challenging for healthcare providers to personalize treatment plans.',
                                'Patients want to be more involved in their healthcare and feel empowered to manage their chronic diseases.',
                            ],
                        },
                        {
                            kind: 'section',
                            heading: 'Market recognition',
                        },
                        {
                            kind: 'grid-cards',
                            items: [
                                {
                                    title: '89% forget',
                                    body: '89% of patients with chronic diseases forget to report their symptoms during medical appointments. Source · Accenture.',
                                },
                                {
                                    title: '$107.7B market',
                                    body: 'There is a growing demand for mobile health apps, with the global mHealth app market projected to reach $107.7 billion by 2026. Source · Grand View Research.',
                                },
                            ],
                        },
                    ],
                },
                // ── Define ───────────────────────────────────────────
                {
                    label: 'Define',
                    blocks: [
                        {
                            kind: 'phase-hero',
                            phase: 'define',
                            number: '02',
                            title: 'Define',
                            items: [
                                'User persona, Elisa',
                                'Goals · wishlist · pain-points',
                            ],
                        },
                        {
                            kind: 'section',
                            heading: 'User persona',
                            paragraph:
                                "Elisa is a 38-year-old patient who has been diagnosed with diabetes and high blood pressure. She takes multiple medications and is struggling to keep track of them. She also has a busy schedule and often forgets to take her medications on time. During the interviews, Elisa mentioned that she needs a “practical tool” that will help her manage her chronic diseases effectively.",
                        },
                        {
                            kind: 'persona',
                            image: '/api/files/fbef8921-9553-4cb4-97ed-70febec62834',
                            name: 'Meet Elisa.',
                            intro:
                                "Full-time employee juggling diabetes and high blood pressure, multiple medications, a busy calendar, and no single place to make sense of it all.",
                            stats: [
                                { label: 'Age', value: '38' },
                                { label: 'Occupation', value: 'Full-time employee' },
                                { label: 'Conditions', value: 'Diabetes · High Blood Pressure' },
                                { label: 'Tool need', value: 'A practical daily tool' },
                            ],
                            goals: [
                                'Track her symptoms and medication',
                                'Be more informed about her condition',
                                'Improve her adherence to treatment plans',
                                'Communicate with her healthcare providers',
                            ],
                            wishlist: [
                                'A personalized app that adapts to her needs',
                                'A way to track her progress over time',
                                'A reminder system for her medication',
                                'A way to share her data with her doctor',
                            ],
                            frustrations: [
                                'Difficulty in remembering to take her medications on time',
                                'Feeling overwhelmed by the complexity of managing her chronic diseases',
                                'Lack of a centralized system to track her symptoms and medications',
                                "Frustration with the current healthcare system's approach to chronic disease management",
                            ],
                        },
                    ],
                },
                // ── Ideate ───────────────────────────────────────────
                {
                    label: 'Ideate',
                    blocks: [
                        {
                            kind: 'phase-hero',
                            phase: 'ideate',
                            number: '03',
                            title: 'Ideate',
                            items: [
                                'How Might We statements',
                                'Design process, Discovery · Ideation · Design',
                                'Sketches, Add & Update Symptoms',
                            ],
                        },
                        {
                            kind: 'section',
                            heading: 'How Might We statements',
                            paragraph:
                                'Every insight from the research phase was reframed as a solvable design question.',
                        },
                        {
                            kind: 'grid-cards',
                            items: [
                                {
                                    title: 'Manage chronic disease',
                                    body: 'How might we create a mobile app that allows patients to manage and track their chronic disease and symptoms?',
                                },
                                {
                                    title: 'Report to providers',
                                    body: 'How might we help patients track their symptoms and report them to their healthcare providers?',
                                },
                                {
                                    title: 'Adherence to plans',
                                    body: 'How might we help patients adhere to their medications and treatment plans?',
                                },
                                {
                                    title: 'Access to information',
                                    body: 'How might we help patients access information and resources about their chronic diseases?',
                                },
                                {
                                    title: 'Communicate with team',
                                    body: 'How might we help patients communicate with their healthcare providers?',
                                },
                            ],
                        },
                        {
                            kind: 'section',
                            heading: 'Design process',
                            paragraph:
                                'Three phases, Discovery, Ideation, and Design, each with clear inputs and outputs.',
                        },
                        {
                            kind: 'grid-cards',
                            items: [
                                {
                                    title: 'Discovery',
                                    body: 'Deep dive into the problems based on user experience and define the problem clearly.',
                                },
                                {
                                    title: 'Ideation',
                                    body: 'Brainstorm ideas, from concept and inspiration all the way to MVP.',
                                },
                                {
                                    title: 'Design',
                                    body: 'Sketch out the product to align with the user needs.',
                                },
                            ],
                        },
                        {
                            kind: 'section',
                            heading: 'Sketches',
                            paragraph:
                                'Early designs of some of the important screens, starting with Add Symptoms and Update Symptoms flows.',
                        },
                        {
                            kind: 'image',
                            src: '/api/files/3f2e0df2-dba2-49d5-b096-bdf48e7a264d',
                            variant: 'sketch',
                        },
                        {
                            kind: 'image',
                            src: '/api/files/da0e7f74-0680-4d07-9cea-71ab7583e0b9',
                            size: 'md',
                            variant: 'sketch',
                        },
                    ],
                },
                // ── Prototype ────────────────────────────────────────
                {
                    label: 'Prototype',
                    blocks: [
                        {
                            kind: 'phase-hero',
                            phase: 'prototype',
                            number: '04',
                            title: 'Prototype',
                            items: [
                                'User flows',
                                'Style guide, colour palette + type',
                                'Visual designs across 7 key screens',
                            ],
                        },
                        {
                            kind: 'section',
                            heading: 'User flows',
                            paragraph:
                                'User Flows are essential to ensure users can navigate the app easily and accomplish their key tasks. Three flows were mapped, Symptom Tracking, Medication Tracking, and Accessing Insights & Analysis.',
                        },
                        {
                            kind: 'image',
                            src: '/api/files/f21a7743-550b-4ce3-b050-f3081edec167',
                        },
                        {
                            kind: 'accordion',
                            items: [
                                {
                                    title: 'Symptom Tracking',
                                    summary: 'Log symptoms with severity, duration & triggers.',
                                    blocks: [
                                        {
                                            kind: 'bullets',
                                            items: [
                                                'User opens the app and navigates to the Symptom Tracking screen.',
                                                'User selects the symptoms they want to log from a pre-defined list or adds a new symptom.',
                                                'User inputs the severity and duration of the symptom.',
                                                'User adds any triggers or patterns they have noticed.',
                                                'User adds the symptom to their log.',
                                            ],
                                        },
                                    ],
                                },
                                {
                                    title: 'Medication Tracking',
                                    summary: 'Add medications, set reminders, track adherence.',
                                    blocks: [
                                        {
                                            kind: 'bullets',
                                            items: [
                                                'User navigates to the Medication Tracking screen.',
                                                'User selects the medication they want to track or adds a new one.',
                                                'User inputs the dosage, frequency and time of day.',
                                                'User sets reminders for when to take the medication.',
                                                'User adds the medication to their log.',
                                            ],
                                        },
                                    ],
                                },
                                {
                                    title: 'Insights & Analysis',
                                    summary: 'Review trends, export data, share with providers.',
                                    blocks: [
                                        {
                                            kind: 'bullets',
                                            items: [
                                                'User navigates to the Insights & Analysis screen.',
                                                'User selects the date range they want to view insights for.',
                                                "App displays an overview of the user's symptoms, medications and any other relevant data for the selected date range.",
                                                'User can view additional insights by selecting specific symptoms or medications.',
                                                'User can export the insights and analysis to share with their healthcare providers.',
                                            ],
                                        },
                                    ],
                                },
                            ],
                        },
                        {
                            kind: 'section',
                            heading: 'Style guide',
                            subheading: 'Colour palette',
                        },
                        {
                            kind: 'color-swatches',
                            items: [
                                { name: 'Deep Blue', hex: '#5200FF', use: 'Primary brand + CTAs' },
                                { name: 'White', hex: '#FFFFFF', use: 'Surfaces' },
                                { name: 'Light Grey', hex: '#F5F5F5', use: 'Backgrounds' },
                                { name: 'Black', hex: '#000000', use: 'Headings' },
                                { name: 'Pale Purple', hex: '#E5E3FF', use: 'Tints & highlights' },
                                { name: 'Dark Grey', hex: '#A1A1A1', use: 'Secondary text' },
                            ],
                        },
                        {
                            kind: 'section',
                            subheading: 'Typeface, Alegreya Sans',
                            paragraph:
                                'Alegreya Sans across headline, subheading, body, buttons and captions, one type family for a consistent, humane read.',
                        },
                        {
                            kind: 'section',
                            heading: 'Visual designs',
                            paragraph:
                                'Seven flows brought to life with the TrackBuddy visual language, from onboarding to daily use.',
                        },
                        {
                            kind: 'sub-tabs',
                            heading: 'Screen flows',
                            tabs: [
                                {
                                    label: 'Login',
                                    blocks: [
                                        {
                                            kind: 'section',
                                            heading: 'Login Screens',
                                            paragraph:
                                                'A warm, doctor-led splash establishes trust, followed by a phone-first Register/Login with OTP verification.',
                                        },
                                        {
                                            kind: 'image',
                                            src: '/api/files/730d6d41-fe53-4e54-b80a-f314642559f9',
                                            size: 'md',
                                            variant: 'phone',
                                        },
                                    ],
                                },
                                {
                                    label: 'Details',
                                    blocks: [
                                        {
                                            kind: 'section',
                                            heading: 'Personal / Health Details',
                                            paragraph:
                                                'Progressive profile setup, personal details, then weight, height, blood group, allergies and chronic illnesses. Every step has a Skip option to reduce friction.',
                                        },
                                        {
                                            kind: 'image',
                                            src: '/api/files/772e71d3-c0a5-4b31-a0fb-428d69edb78d',
                                            size: 'md',
                                            variant: 'phone',
                                        },
                                    ],
                                },
                                {
                                    label: 'Home',
                                    blocks: [
                                        {
                                            kind: 'section',
                                            heading: 'Landing Page',
                                            paragraph:
                                                'A daily "Any Symptoms Today?" prompt anchors the home screen, with Symptoms to Update / Patterns Discovered / Patterns to Validate summary cards and a prominent add-symptom action.',
                                        },
                                        {
                                            kind: 'image',
                                            src: '/api/files/cc0eb9c2-71b8-453c-a209-25cd2d9e772f',
                                            size: 'sm',
                                            variant: 'phone',
                                        },
                                    ],
                                },
                                {
                                    label: 'Add Symptoms',
                                    blocks: [
                                        {
                                            kind: 'section',
                                            heading: 'Add Symptoms Screens',
                                            paragraph:
                                                'An empty state, a searchable symptom dictionary and a detailed capture flow, first occurrence, occurrence pattern, intensity slider, and rich context (notes / voice memo / photo), before adding to the tracked list.',
                                        },
                                        {
                                            kind: 'image',
                                            src: '/api/files/eeaffceb-804f-44ac-98d7-a704fa6ba72a',
                                            size: 'md',
                                            variant: 'phone',
                                        },
                                    ],
                                },
                                {
                                    label: 'Update Symptoms',
                                    blocks: [
                                        {
                                            kind: 'section',
                                            heading: 'Update Symptoms Screens',
                                            paragraph:
                                                'A calendar-first view of tracked symptoms; each collapses open to a "Do you feel this symptom today?" nudge with time-boxed logging (start / end / all-day) and the same rich-media inputs from capture.',
                                        },
                                        {
                                            kind: 'image',
                                            src: '/api/files/922e25ea-3e4f-4201-9b2b-50e3979cbee8',
                                            size: 'md',
                                            variant: 'phone',
                                        },
                                    ],
                                },
                                {
                                    label: 'Profile',
                                    blocks: [
                                        {
                                            kind: 'section',
                                            heading: 'Profile Page',
                                            paragraph:
                                                'Minimal identity card with Account Settings, About TrackBuddy, Privacy Policy and Log Out, designed to feel calm and never intrusive.',
                                        },
                                        {
                                            kind: 'image',
                                            src: '/api/files/f3c361e3-263c-4498-86a0-f43366cb5ab6',
                                            size: 'sm',
                                            variant: 'phone',
                                        },
                                    ],
                                },
                                {
                                    label: 'Medication',
                                    blocks: [
                                        {
                                            kind: 'section',
                                            heading: 'Medication Reminder / Tracking Screens',
                                            paragraph:
                                                'Empty-state prompt to add a medicine, an at-a-glance schedule sorted by Morning/Afternoon/Evening, a Log Medications sheet with Skip / Snooze / Take now, and a full Add Medications form with dose, reminder times and low-stock alert.',
                                        },
                                        {
                                            kind: 'image',
                                            src: '/api/files/d6c2afc5-6ba7-478a-b1b4-c06dd5fd40e3',
                                            size: 'md',
                                            variant: 'phone',
                                        },
                                    ],
                                },
                            ],
                        },
                    ],
                },
                // ── Deliver ──────────────────────────────────────────
                {
                    label: 'Deliver',
                    blocks: [
                        {
                            kind: 'phase-hero',
                            phase: 'deliver',
                            number: '05',
                            title: 'Deliver',
                            items: [
                                'Seamless user experience',
                                'Better health outcomes',
                            ],
                        },
                        {
                            kind: 'section',
                            heading: 'Outcome',
                            paragraph:
                                'These user flows make it easy for users to accomplish their key tasks within the app. By providing a seamless user experience, the app helps users better manage and track their chronic diseases and improve their health outcomes.',
                        },
                    ],
                },
            ],
        },
    ],
    next: { slug: 'travel-app', title: 'Travel App For Visitors' },
};
